import { Menu, MenuItem, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { dayjs, isNotNullOrUndefined } from '@weft/utils';
import classNames from 'classnames';
import type { Dayjs } from 'dayjs';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useMenu } from '../../../../hooks/use-menu';
import { Divider } from '../../../design/divider/Divider';

type VyRangePickerMenuProps = {
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onStartDateChange: (startDate: Dayjs | null) => void;
  onEndDateChange: (endDate: Dayjs | null) => void;
  children?: React.ReactElement;
};

export const VyRangePickerMenu: React.FC<VyRangePickerMenuProps> = ({
  fromDate,
  toDate,
  onStartDateChange,
  onEndDateChange,
  children,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();

  const handleRangeChange = (startDate: Dayjs, endDate: Dayjs) => {
    onStartDateChange?.(startDate);
    onEndDateChange?.(endDate);
    handleMenuClose();
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    onStartDateChange?.(date);
    handleMenuClose();
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    onEndDateChange?.(date);
    handleMenuClose();
  };

  return (
    <>
      {isNotNullOrUndefined(children) && React.cloneElement(children, {
        onClick: handleMenuOpen,
      })}

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 50,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 50,
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
      >

        <div
          className="px-7"
        >
          <Typography
            color="text.secondary"
            variant="caption"
          >
            Select Range
          </Typography>
          <div className="mt-1 flex items-center justify-between gap-2">
            <DatePicker
              onChange={handleStartDateChange}
              value={fromDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: '180px',
                  },
                },
              }}
            />

            <div>-</div>

            <DatePicker
              onChange={handleEndDateChange}
              value={toDate}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: '180px',
                  },
                },
              }}
            />
          </div>

        </div>

        <div className="px-7 pt-2">
          <Divider className="my-2" />
        </div>

        {menuItems.map((item) => {
          return (
            <MenuItem
              key={item.label}
              className={classNames(twMerge('flex cursor-pointer items-center py-1.5 px-7 gap-2 hover:bg-input'))}
              onClick={() => handleRangeChange(item.from, item.to)}
            >
              {item.label}
            </MenuItem>
          );
        })}

      </Menu>
    </>
  );
};

const startOfDay = dayjs().startOf('day');
const endOfDay = dayjs().endOf('day');

const menuItems: Array<{
  from: Dayjs,
  to: Dayjs,
  label: string,
}> = [
  {
    from: startOfDay,
    to: endOfDay,
    label: 'Today',
  },
  {
    from: getLastDays(7),
    to: endOfDay,
    label: 'Last 7 days',
  },
  {
    from: getLastDays(30),
    to: endOfDay,
    label: 'Last 30 days',
  },
  {
    from: getLastMonths(6),
    to: endOfDay,
    label: 'Last 6 months',
  },
  {
    from: getLastYears(1),
    to: endOfDay,
    label: 'Last year',
  },
];

function getLastDays(days: number) {
  return startOfDay.subtract(days, 'days');
}

function getLastMonths(months: number) {
  return startOfDay.subtract(months, 'months');
}

function getLastYears(years: number) {
  return startOfDay.subtract(years, 'years');
}